import React, { useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, Heart, ListMusic, X, Download, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { coverFor, fmtTime, Waveform } from "@/components/common";
import { Slider } from "@/components/ui/slider";
import api from "@/lib/api";
import { toast } from "sonner";

export function Player({ onDownload, onLyricVideo }) {
  const { current, playing, progress, duration, volume, setVolume, toggle, next, prev, seek, queue } = usePlayer();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showQueue, setShowQueue] = useState(false);

  if (!current) return null;

  const like = async () => {
    if (!user) return navigate("/login");
    try { await api.post(`/songs/${current.id}/like`); toast.success("Liked"); } catch {}
  };

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-40 jx-glass border-t border-white/10 lg:left-[260px]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="persistent-player"
      >
        <div className="h-0.5 bg-white/5 cursor-pointer" onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          seek(((e.clientX - r.left) / r.width) * duration);
        }}>
          <div className="h-full jx-cta" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
        </div>
        <div className="flex items-center gap-3 px-3 sm:px-4 py-2.5">
          <div className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => setExpanded(true)}>
            <img src={coverFor(current)} className="w-11 h-11 rounded-lg object-cover" alt="" />
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate text-slate-100" data-testid="player-title">{current.title}</p>
              <p className="text-xs text-slate-400 truncate">{current.creator_name}</p>
            </div>
            {playing && <Waveform bars={4} className="hidden sm:flex" />}
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={prev} className="p-2 text-slate-400 hover:text-white hidden sm:block" data-testid="player-prev"><SkipBack size={18} /></button>
            <button onClick={toggle} className="w-10 h-10 rounded-full jx-cta grid place-items-center text-white" data-testid="player-toggle">
              {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <button onClick={next} className="p-2 text-slate-400 hover:text-white" data-testid="player-next"><SkipForward size={18} /></button>
          </div>

          <div className="hidden lg:flex items-center gap-3 flex-1 justify-end">
            <span className="text-[11px] text-slate-400 font-mono">{fmtTime(progress)} / {fmtTime(duration)}</span>
            <button onClick={like} className="p-2 text-slate-400 hover:text-rose-400" data-testid="player-like"><Heart size={17} /></button>
            <button onClick={() => onLyricVideo?.(current)} className="p-2 text-slate-400 hover:text-cyan-400" title="Lyric video"><Video size={17} /></button>
            <button onClick={() => onDownload?.(current)} className="p-2 text-slate-400 hover:text-purple-400" title="Download"><Download size={17} /></button>
            <button onClick={() => setShowQueue(true)} className="p-2 text-slate-400 hover:text-white"><ListMusic size={17} /></button>
            <div className="flex items-center gap-2 w-28">
              <Volume2 size={16} className="text-slate-400" />
              <Slider value={[volume * 100]} max={100} step={1} onValueChange={(v) => setVolume(v[0] / 100)} />
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="fixed inset-0 z-50 jx-radial-bloom bg-[#09090b] flex flex-col p-6 animate-fadeup" data-testid="player-expanded">
          <button onClick={() => setExpanded(false)} className="self-end p-2 text-slate-400"><X /></button>
          <div className="flex-1 flex flex-col items-center justify-center gap-6 max-w-md mx-auto w-full">
            <img src={coverFor(current)} className="w-64 h-64 sm:w-72 sm:h-72 rounded-3xl object-cover jx-glow-purple" alt="" />
            <div className="text-center">
              <h2 className="text-2xl font-bold font-display">{current.title}</h2>
              <p className="text-slate-400 mt-1">{current.creator_name}</p>
            </div>
            {playing && <Waveform bars={14} className="h-8" />}
            <div className="w-full">
              <Slider value={[duration ? (progress / duration) * 100 : 0]} max={100} step={0.1}
                onValueChange={(v) => seek((v[0] / 100) * duration)} />
              <div className="flex justify-between text-xs text-slate-400 font-mono mt-1">
                <span>{fmtTime(progress)}</span><span>{fmtTime(duration)}</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <button onClick={prev} className="text-slate-300"><SkipBack size={26} /></button>
              <button onClick={toggle} className="w-16 h-16 rounded-full jx-cta grid place-items-center text-white">
                {playing ? <Pause size={26} /> : <Play size={26} className="ml-1" />}
              </button>
              <button onClick={next} className="text-slate-300"><SkipForward size={26} /></button>
            </div>
            <div className="flex items-center gap-8 mt-2">
              <button onClick={like} className="flex flex-col items-center gap-1 text-slate-400 hover:text-rose-400 text-xs"><Heart size={20} /> Like</button>
              <button onClick={() => { onLyricVideo?.(current); setExpanded(false); }} className="flex flex-col items-center gap-1 text-slate-400 hover:text-cyan-400 text-xs"><Video size={20} /> Lyric Video</button>
              <button onClick={() => onDownload?.(current)} className="flex flex-col items-center gap-1 text-slate-400 hover:text-purple-400 text-xs"><Download size={20} /> Download</button>
            </div>
          </div>
        </div>
      )}

      {showQueue && (
        <div className="fixed inset-0 z-50 bg-black/60" onClick={() => setShowQueue(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] jx-glass p-4 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Queue</h3>
              <button onClick={() => setShowQueue(false)}><X size={18} /></button>
            </div>
            {queue.map((s, i) => (
              <div key={s.id} className={`flex items-center gap-2 p-2 rounded-lg ${s.id === current.id ? "bg-purple-500/15" : ""}`}>
                <img src={coverFor(s)} className="w-9 h-9 rounded object-cover" alt="" />
                <div className="min-w-0"><p className="text-sm truncate">{s.title}</p><p className="text-xs text-slate-500 truncate">{s.creator_name}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
